import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BadgePercent, Loader2, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Section } from '@/components/common/Blocks'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { confirmModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useAccessProjects, useAccessUsers, useRoleOptions } from '@/features/access/api'
import { useCustomers } from '@/features/crm/api'
import { toastError } from '@/features/finance/ui'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'

type Scope = 'user' | 'role' | 'project' | 'account'
const SCOPES: Scope[] = ['user', 'role', 'project', 'account']

type Limit = { id: string; scope: Scope; targetId: string; targetName: string; maxPercent: string; note: string }

const KEY = ['sales', 'discount-limits'] as const

function useLimits() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () =>
      asList(await api.get<Record<string, unknown>[]>('/api/v1/sales/discount-limits')).map(
        (r): Limit => ({
          id: String(r.id ?? ''),
          scope: (String(r.scope ?? 'user') as Scope),
          targetId: String(r.target_id ?? ''),
          targetName: String(r.target_name ?? ''),
          maxPercent: Number(r.max_percent ?? 0).toFixed(2),
          note: String(r.note ?? ''),
        }),
      ),
  })
}

function useLimitMut<A>(fn: (a: A) => Promise<unknown>) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: KEY }) })
}

/**
 * Firma sahibinin azami iskonto tanımları. Teklif/siparişte gerçek iskonto (fiyat düşürme + kalem +
 * genel) kişiye uyan limitlerin en düşüğünü aşamaz; limiti aşma yalnız ayrı yetkiyle mümkündür.
 */
export function DiscountLimitsPage() {
  const { t } = useTranslation()
  const limits = useLimits()
  const save = useLimitMut((b: { scope: Scope; target_id: string; max_percent: string; note: string }) => api.put('/api/v1/sales/discount-limits', b))
  const remove = useLimitMut((id: string) => api.del(`/api/v1/sales/discount-limits/${id}`))
  const users = useAccessUsers()
  const roles = useRoleOptions()
  const projects = useAccessProjects()
  const customers = useCustomers()

  const [scope, setScope] = useState<Scope>('role')
  const [target, setTarget] = useState('')
  const [max, setMax] = useState('')
  const [note, setNote] = useState('')

  const options: Record<Scope, { value: string; label: string }[]> = {
    user: (users.data ?? []).map((u) => ({ value: u.id, label: u.name || u.email })),
    role: (roles.data ?? []).map((r) => ({ value: r.name, label: r.name })),
    project: (projects.data ?? []).map((p) => ({ value: p.id, label: p.name })),
    account: (customers.data ?? []).map((c) => ({ value: c.id, label: c.name })),
  }
  const valid = target !== '' && /^\d{1,3}(\.\d{1,2})?$/.test(max) && Number(max) <= 100

  const onSave = () =>
    save.mutate(
      { scope, target_id: target, max_percent: max, note },
      {
        onSuccess: () => {
          toast.success(t('admin.discounts.saved'))
          setTarget('')
          setMax('')
          setNote('')
        },
        onError: toastError,
      },
    )
  const onDelete = async (l: Limit) => {
    if (!(await confirmModal({ title: t('admin.discounts.deleteTitle'), description: t('admin.discounts.deleteBody', { name: l.targetName }), confirmLabel: t('common.delete'), destructive: true }))) return
    remove.mutate(l.id, { onSuccess: () => toast.success(t('common.deleted')), onError: toastError })
  }

  return (
    <>
      <PageHeader eyebrow={t('nav.groups.admin')} title={t('admin.discounts.title')} description={t('admin.discounts.subtitle')} />
      <div className="space-y-4">
        <Section title={t('admin.discounts.add')} icon={Plus}>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[10rem_minmax(0,1fr)_8rem_minmax(0,1fr)_auto] md:items-end">
            <div className="space-y-1.5">
              <Label htmlFor="dl-scope">{t('admin.discounts.scope')}</Label>
              <Select
                id="dl-scope"
                value={scope}
                onChange={(v) => {
                  setScope(v)
                  setTarget('')
                }}
                options={SCOPES.map((s) => ({ value: s, label: t(`admin.discounts.scopes.${s}`) }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dl-target">{t('admin.discounts.target')}</Label>
              <Select id="dl-target" value={target} onChange={setTarget} searchable options={options[scope]} placeholder={t('admin.discounts.targetPlaceholder')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dl-max">{t('admin.discounts.max')}</Label>
              <Input id="dl-max" inputMode="decimal" value={max} onChange={(e) => setMax(e.target.value)} placeholder="10" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dl-note">{t('admin.discounts.note')}</Label>
              <Input id="dl-note" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <Button onClick={onSave} disabled={!valid || save.isPending}>
              {save.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
              {t('common.save')}
            </Button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">{t('admin.discounts.rule')}</p>
        </Section>

        <Section title={t('admin.discounts.listTitle')} icon={BadgePercent} bodyClassName="p-0">
          {limits.isError ? (
            <ErrorState error={limits.error} onRetry={() => void limits.refetch()} />
          ) : limits.isLoading ? (
            <Skeleton className="m-4 h-32" />
          ) : !limits.data?.length ? (
            <EmptyState icon={BadgePercent} title={t('admin.discounts.empty')} description={t('admin.discounts.emptyHint')} />
          ) : (
            <ul className="divide-y">
              {limits.data.map((l) => (
                <li key={l.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <Badge tone="neutral">{t(`admin.discounts.scopes.${l.scope}`)}</Badge>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{l.targetName || l.targetId}</span>
                  {l.note ? <span className="truncate text-xs text-muted-foreground">{l.note}</span> : null}
                  <span className="w-20 text-right font-semibold tabular-nums">%{l.maxPercent}</span>
                  <Button size="icon" variant="ghost" aria-label={t('common.delete')} onClick={() => void onDelete(l)}>
                    <Trash2 />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </>
  )
}
